import { ReactNode, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { SlArrowDown, SlArrowUp } from 'react-icons/sl';

import { useSearchParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import {
  Box,
  Button,
  Checkbox,
  Group,
  Flex,
  Collapse,
  Text,
  SegmentedControl,
  Menu,
} from '@mantine/core';
import ThDateRangePicker from '@components/elements/ThDateRangePicker/ThDateRangePicker';
import ThSearchInput from '@components/elements/ThSearchInput/ThSearchInput';
import {
  ComHubQueryParamsEnum,
  ChatModeEnum,
} from '@company/types/comm-hub.type';
import DATE_FORMATS from '@constants/dateFormat';
import { SEARCH_DEBOUNCE_MS } from '@constants/various';

interface FilterBarProps {
  onSearch: (searchObject: {
    searchText: string;
    dateRange: { startDate: Date | undefined; endDate: Date | undefined };
  }) => void;
  onShowOnlyNew?: () => void;
  showOnlyNew?: boolean;
  isMessagingTab?: boolean;
  extraAction?: ReactNode;
  chatMode?: ChatModeEnum | null;
  onChatModeChange?: (mode: ChatModeEnum | null) => void;
  onMarkAllAsRead?: () => void;
  onMarkAllAsUnread?: () => void;
  markAllAsUnreadLabel?: string;
  onMarkUnreadMessages?: () => void;
  markUnreadMessagesLabel?: string;
}

function FilterBar({
  onSearch,
  onShowOnlyNew,
  showOnlyNew,
  isMessagingTab,
  extraAction,
  chatMode,
  onChatModeChange,
  onMarkAllAsRead,
  onMarkAllAsUnread,
  markAllAsUnreadLabel = 'Mark page as unread',
  onMarkUnreadMessages,
  markUnreadMessagesLabel = 'Mark unread on this page as read',
}: FilterBarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(
    searchParams.get(ComHubQueryParamsEnum.SEARCH_TEXT) || ''
  );
  const [pickedDateRange, setPickedDateRange] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({
    startDate: searchParams.get(ComHubQueryParamsEnum.DATE_FROM)
      ? new Date(searchParams.get(ComHubQueryParamsEnum.DATE_FROM)!)
      : undefined,
    endDate: searchParams.get(ComHubQueryParamsEnum.DATE_TO)
      ? new Date(searchParams.get(ComHubQueryParamsEnum.DATE_TO)!)
      : undefined,
  });
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const debouncedSearch = useDebouncedCallback(
    (
      value: string,
      dateRange: { startDate: Date | undefined; endDate: Date | undefined }
    ) => {
      if (value.length === 0 || value.length >= 3) {
        onSearch({ searchText: value, dateRange });
      }
    },
    SEARCH_DEBOUNCE_MS
  );

  const handleShowOnlyNew = () => {
    const paramToUse = isMessagingTab
      ? ComHubQueryParamsEnum.UNREAD
      : ComHubQueryParamsEnum.NEW;

    // Reset offset to 0 when filter changes
    searchParams.set(ComHubQueryParamsEnum.OFFSET, '0');

    if (searchParams.get(paramToUse)) {
      searchParams.delete(paramToUse);
    } else {
      searchParams.set(paramToUse, 'true');
    }
    setSearchParams(searchParams);
    if (onShowOnlyNew) {
      onShowOnlyNew();
    }
  };

  const handleSearchOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.toLowerCase();
    setSearchValue(inputValue);
    debouncedSearch(inputValue, pickedDateRange);
  };

  const handleDateRangeChange = (
    dateRange: Array<{ startDate: Date; endDate: Date }>
  ) => {
    const { startDate, endDate } = dateRange[0];

    setPickedDateRange({ startDate, endDate });
    const isCompleteRange =
      startDate && endDate && startDate.getTime() !== endDate.getTime();
    const isCleared = !startDate && !endDate;

    if (isCompleteRange || isCleared) {
      debouncedSearch(searchValue, { startDate, endDate });
    }
  };

  const handleSearch = () => {
    onSearch({
      searchText: searchValue,
      dateRange: {
        startDate: pickedDateRange.startDate,
        endDate: pickedDateRange.endDate,
      },
    });
  };

  return (
    <div className="com-hub-filter-bar">
      {isMobile && (
        <Group
          justify="space-between"
          align="center"
          mb="md"
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{ cursor: 'pointer' }}
        >
          <Text fw={700}>Filters</Text>
          <Box pr="sm">
            {isCollapsed ? (
              <FaChevronDown size={20} />
            ) : (
              <FaChevronUp size={20} />
            )}
          </Box>
        </Group>
      )}
      {!isMobile && (
        <Flex direction="row" gap="sm" mb="md" align="center" wrap="wrap">
          {onChatModeChange && (
            <SegmentedControl
              size="md"
              value={chatMode ?? 'all'}
              onChange={(value) =>
                onChatModeChange(
                  value === 'all' ? null : (value as ChatModeEnum)
                )
              }
              data={[
                { label: 'Inbox', value: ChatModeEnum.INBOX },
                { label: 'Sent', value: ChatModeEnum.SENT },
                { label: 'All', value: 'all' },
              ]}
              styles={{
                label: { paddingLeft: '30px', paddingRight: '30px' },
                control: { flex: 1 },
              }}
            />
          )}
          <Box ml="auto" style={{ flex: 1 }}>
            <Group gap="sm" align="center" wrap="nowrap" justify="flex-end">
              {onShowOnlyNew && (
                <Checkbox
                  label="Unread"
                  checked={showOnlyNew}
                  onChange={handleShowOnlyNew}
                  styles={{
                    body: {
                      borderRadius: '4px',
                      padding: '8px 12px',
                      alignItems: 'center',
                      cursor: 'pointer',
                      backgroundColor: 'var(--mantine-color-gray-1)',
                    },
                  }}
                />
              )}
              <Box style={{ maxWidth: '320px', width: '100%' }}>
                <ThSearchInput
                  value={searchValue}
                  name="received-reviews"
                  placeholder="Search"
                  onChange={handleSearchOnChange}
                  useGrayBackground
                  onClear={() => {
                    setSearchValue('');
                    debouncedSearch('', pickedDateRange);
                  }}
                />
              </Box>
              <ThDateRangePicker
                start={pickedDateRange.startDate}
                end={pickedDateRange.endDate}
                setStart={() => undefined}
                setEnd={() => undefined}
                onChange={handleDateRangeChange}
                useGrayBackground
                calendarPosition="right"
                displayFormat={DATE_FORMATS.DATE_FORMAT_SHORT}
              />
              <Button onClick={handleSearch}>Search</Button>
              {(onMarkAllAsRead ||
                onMarkAllAsUnread ||
                onMarkUnreadMessages) && (
                <Menu
                  position="bottom-start"
                  opened={isMenuOpen}
                  onChange={setIsMenuOpen}
                >
                  <Menu.Target>
                    <Button
                      variant="light"
                      leftSection={
                        isMenuOpen ? (
                          <SlArrowUp size={18} />
                        ) : (
                          <SlArrowDown size={18} />
                        )
                      }
                    >
                      More
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {onMarkAllAsRead && (
                      <Menu.Item onClick={onMarkAllAsRead}>
                        Mark all as read
                      </Menu.Item>
                    )}
                    {onMarkAllAsUnread && (
                      <Menu.Item onClick={onMarkAllAsUnread}>
                        {markAllAsUnreadLabel}
                      </Menu.Item>
                    )}
                    {onMarkUnreadMessages && (
                      <Menu.Item onClick={onMarkUnreadMessages}>
                        {markUnreadMessagesLabel}
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Box>
          {extraAction && <Box ml="auto">{extraAction}</Box>}
        </Flex>
      )}
      <Collapse in={!isCollapsed}>
        {isMobile && (
          <Flex direction="column" gap="xs" mb="md">
            {onChatModeChange && (
              <SegmentedControl
                fullWidth
                size="md"
                value={chatMode ?? 'all'}
                onChange={(value) =>
                  onChatModeChange(
                    value === 'all' ? null : (value as ChatModeEnum)
                  )
                }
                data={[
                  { label: 'Inbox', value: ChatModeEnum.INBOX },
                  { label: 'Sent', value: ChatModeEnum.SENT },
                  { label: 'All', value: 'all' },
                ]}
                styles={{
                  label: { paddingLeft: '30px', paddingRight: '30px' },
                  control: { flex: 1 },
                }}
              />
            )}
            <Group gap="sm" wrap="nowrap">
              <Box style={{ flex: 1 }}>
                <ThSearchInput
                  value={searchValue}
                  name="received-reviews"
                  placeholder="Search"
                  onChange={handleSearchOnChange}
                  useGrayBackground
                  onClear={() => {
                    setSearchValue('');
                    debouncedSearch('', pickedDateRange);
                  }}
                />
              </Box>
              <Button onClick={handleSearch}>Search</Button>
            </Group>
            <ThDateRangePicker
              start={pickedDateRange.startDate}
              end={pickedDateRange.endDate}
              setStart={() => undefined}
              setEnd={() => undefined}
              onChange={handleDateRangeChange}
              useGrayBackground
            />
            {onShowOnlyNew && (
              <Checkbox
                label="Unread"
                checked={showOnlyNew}
                onChange={handleShowOnlyNew}
                styles={{
                  body: {
                    borderRadius: '4px',
                    padding: '8px 12px',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: 'var(--mantine-color-gray-1)',
                    width: '100%',
                  },
                }}
              />
            )}
          </Flex>
        )}
      </Collapse>
    </div>
  );
}

export default FilterBar;
